import { applyOrgFilter, getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

/** Parse 'YYYY-MM-DD' as local midnight (not UTC) to avoid timezone off-by-one bugs */
function parseLocalDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

export type MonthlyClientEntry = {
  teamId: string;
  clientName: string;
  monthlyRevenue: Record<string, number>;
  monthlyCogs: Record<string, number>;
  // Actual contractor cost from Warp payroll (allocation-weighted, per client).
  monthlyActualCogs: Record<string, number>;
};

export type MonthlyClientBreakdown = {
  months: string[];
  forecastStartMonth: string | null;
  totalRevenue: Record<string, number>;
  totalCogs: Record<string, number>;
  // Actual contractor payroll spend (USD) per month, from Warp bulk reports.
  totalActualCogs: Record<string, number>;
  // Operational people (internal team members) — planned rate cost and actual payroll.
  totalOpsPlan: Record<string, number>;
  totalOpsActual: Record<string, number>;
  totalOpex: Record<string, number>;
  clients: MonthlyClientEntry[];
};

export type DrillDownRow = {
  id: string;
  date: string;
  description: string;
  amount: number;
  vendor?: string;
  category?: string;
};

export type CogsDrillDownRow = {
  id: string;
  memberName: string;
  teamName: string;
  role: string;
  monthlyRate: number;
  allocationPct: number;
  cost: number;
  /**
   * Which basis this row came from. Must match what the Monthly Breakdown's
   * COGS cell used for the same month, or the drill-down explains a different
   * number from the one that was clicked.
   */
  basis: 'actual' | 'plan';
};

export type RevenueForecastDrillDownRow = {
  id: string;
  memberName: string;
  teamName: string;
  clientRate: number;
  clientRateType: 'hourly' | 'monthly';
  allocationPct: number;
  projectedRevenue: number;
};

export type ClientTeamMember = {
  id: string;
  profileId: string;
  firstName: string;
  lastName: string;
  developerType: string | null;
  role: string;
  monthlyRate: number | null;
  clientRate: number | null;
  clientRateType: 'hourly' | 'monthly';
  allocationPct: number;
  startDate: string | null;
  endDate: string | null;
};

export type ClientProfitability = {
  teamId: string;
  teamName: string;
  category: string | null;
  revenue: number;
  allocatedCost: number;
  profit: number;
  margin: number;
  members: ClientTeamMember[];
  revenueTransactionCount: number;
  /**
   * Where `revenue` came from.
   *   rate_card   — estimated from contracted client rates (what this report uses)
   *   invoice     — accrual, billed and possibly unpaid
   *   transaction — cash actually received
   * The card labels the count from this, so an estimate is never presented as
   * money billed or received.
   */
  revenueSource: 'rate_card' | 'invoice' | 'transaction';
};

export type ClientDetail = {
  teamId: string;
  teamName: string;
  members: ClientTeamMember[];
};

export type MonthlyPnLRow = {
  month: string;
  revenue: number;
  cogs: number;
  grossProfit: number;
  grossMarginPct: number;
  opex: number;
  netIncome: number;
  savingsTarget: number;
  surplus: number;
  isForecast: boolean;
};

export type MonthlyPnLData = {
  rows: MonthlyPnLRow[];
  ytdRevenue: number;
  ytdCogs: number;
  ytdGrossProfit: number;
  ytdOpex: number;
  ytdNetIncome: number;
  ytdSavingsTarget: number;
  ytdSurplus: number;
  avgMonthlySavingsRate: number;
  warnings: string[];
};

const financeClientsService = {
  async getClientProfitability({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<ClientProfitability[]> {
    // 1. Get all teams that are clients (have linked vendors with income transactions)
    let vendorsQuery = supabase
      .from('fx_vendors')
      .select('id, name, team_id, teams(id, name, category)')
      .not('team_id', 'is', null);
    vendorsQuery = applyOrgFilter(vendorsQuery);
    const { data: vendorsWithTeams, error: vendorsError } = await vendorsQuery;
    if (vendorsError) throw vendorsError;

    if (!vendorsWithTeams?.length) return [];

    // 2. Revenue is ESTIMATED from the rate card, not read from invoices.
    //
    //    Deliberate: this report answers "what is this engagement worth at
    //    contracted rates", which is a planning question, not an accounting one.
    //    It is therefore an estimate and is labelled as such in the UI.
    //
    //    The trade-off is real and was measured before switching. For June 2026
    //    the rate card said $261,564 across all clients while $175,913 was
    //    actually invoiced — 49% high. Four clients landed within 5%, but
    //    Connected Performance was 40% out and Flyreel had not been invoiced at
    //    all. So these figures show contracted capacity, NOT billed revenue, and
    //    they will not reconcile to QuickBooks.
    //
    //    Actual invoiced revenue remains available on the Invoices page and in
    //    the dashboard's Monthly Breakdown, both of which still use invoices.
    //
    //    Revenue is prorated by days on the team over the selected period, using
    //    the same overlap arithmetic as allocatedCost below. Without that, a
    //    person who joined mid-period would contribute a full month of revenue
    //    against a part month of cost and the margin would be nonsense.

    // 3. Get team members with profiles for all linked teams
    const teamIds = vendorsWithTeams.map((v: any) => v.team_id).filter(Boolean);
    const { data: membersData } = await supabase
      .from('team_members')
      .select(
        'id, team_id, profile_id, role, monthly_rate, client_rate, client_rate_type, allocation_pct, start_date, end_date, profiles(first_name, last_name, developer_type)'
      )
      .in('team_id', teamIds);

    // 4. Calculate months in range for cost allocation
    // Parse from strings to avoid timezone issues (new Date('YYYY-MM-DD').getMonth() uses local TZ)
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const monthsInRange = Math.max(1, (endYear - startYear) * 12 + (endMonth - startMonth) + 1);

    // 5. Group vendors by team to avoid duplicates (e.g. Stamp + ALMA TV → same team)
    const teamMap = new Map<
      string,
      { teamId: string; teamName: string; category: string | null; vendorIds: string[] }
    >();
    for (const vendor of vendorsWithTeams) {
      const team = (vendor as any).teams;
      if (!team) continue;
      const existing = teamMap.get(team.id);
      if (existing) {
        existing.vendorIds.push(vendor.id);
      } else {
        teamMap.set(team.id, {
          teamId: team.id,
          teamName: team.name,
          category: team.category,
          vendorIds: [vendor.id],
        });
      }
    }

    // 6. Build profitability per team
    const results: ClientProfitability[] = [];

    for (const teamEntry of teamMap.values()) {
      // Revenue is computed further down, from each member's client rate,
      // because it depends on the deduplicated member list.
      let totalRevenue = 0;
      let totalRevenueCount = 0;

      // Get members for this team — deduplicate by profile_id to prevent
      // double-counting when a person has multiple records for the same team
      const rawMembers = (membersData ?? [])
        .filter((m: any) => m.team_id === teamEntry.teamId)
        .map((m: any) => ({
          id: m.id,
          profileId: m.profile_id,
          firstName: m.profiles?.first_name ?? '',
          lastName: m.profiles?.last_name ?? '',
          developerType: m.profiles?.developer_type ?? null,
          role: m.role,
          monthlyRate: m.monthly_rate != null ? parseFloat(m.monthly_rate) : null,
          clientRate: m.client_rate != null ? parseFloat(m.client_rate) : null,
          clientRateType: m.client_rate_type ?? 'hourly',
          allocationPct: m.allocation_pct ?? 100,
          startDate: m.start_date,
          endDate: m.end_date,
        }));

      // Keep only the most recent record per profile (prefer active over ended, then latest start_date)
      const membersByProfile = new Map<string, (typeof rawMembers)[0]>();
      for (const m of rawMembers) {
        const existing = membersByProfile.get(m.profileId);
        if (!existing) {
          membersByProfile.set(m.profileId, m);
          continue;
        }
        const existingActive =
          !existing.endDate || existing.endDate >= new Date().toISOString().split('T')[0];
        const newActive = !m.endDate || m.endDate >= new Date().toISOString().split('T')[0];
        // Prefer active over inactive, then latest start date
        if (
          (!existingActive && newActive) ||
          (existingActive === newActive && (m.startDate ?? '') > (existing.startDate ?? ''))
        ) {
          membersByProfile.set(m.profileId, m);
        }
      }
      const teamMembers = Array.from(membersByProfile.values());

      // Calculate allocated cost
      let allocatedCost = 0;
      for (const member of teamMembers) {
        // A member with neither rate contributes nothing to either side.
        if (member.monthlyRate == null && member.clientRate == null) continue;

        // Check if member was active during the date range
        const memberStart = member.startDate
          ? parseLocalDate(member.startDate)
          : parseLocalDate('2020-01-01');
        const memberEnd = member.endDate
          ? parseLocalDate(member.endDate)
          : parseLocalDate('2099-12-31');

        // Overlap period (pro-rata by days)
        const overlapStart = new Date(Math.max(start.getTime(), memberStart.getTime()));
        const overlapEnd = new Date(Math.min(end.getTime(), memberEnd.getTime()));
        if (overlapEnd < overlapStart) continue;

        const overlapDays =
          Math.round((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const totalDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const effectiveMonths = (overlapDays / totalDays) * monthsInRange;
        const allocation = member.allocationPct / 100;

        if (member.monthlyRate != null) {
          allocatedCost += member.monthlyRate * allocation * effectiveMonths;
        }

        // Estimated revenue: the client rate over the same prorated period.
        // Hourly rates assume a 160-hour month, the same assumption the member
        // table has always used.
        if (member.clientRate != null) {
          const monthlyClientRate =
            member.clientRateType === 'hourly' ? member.clientRate * 160 : member.clientRate;
          totalRevenue += monthlyClientRate * allocation * effectiveMonths;
          totalRevenueCount += 1;
        }
      }

      const revenue = Math.round(totalRevenue * 100) / 100;
      const cost = Math.round(allocatedCost * 100) / 100;
      const profit = Math.round((revenue - cost) * 100) / 100;
      const margin = revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : 0;

      // Show all clients — new clients with no activity yet should still be visible
      // so users can add team members and set rates

      results.push({
        teamId: teamEntry.teamId,
        teamName: teamEntry.teamName,
        category: teamEntry.category,
        revenue,
        allocatedCost: cost,
        profit,
        margin,
        members: teamMembers,
        revenueTransactionCount: totalRevenueCount,
        revenueSource: 'rate_card',
      });
    }

    return results.sort((a, b) => b.revenue - a.revenue);
  },

  async getClientMembers(teamId: string): Promise<ClientTeamMember[]> {
    const { data, error } = await supabase
      .from('team_members')
      .select(
        'id, team_id, profile_id, role, monthly_rate, client_rate, client_rate_type, allocation_pct, start_date, end_date, profiles(first_name, last_name, developer_type)'
      )
      .eq('team_id', teamId);

    if (error) throw error;

    const all = (data ?? []).map((m: any) => ({
      id: m.id,
      profileId: m.profile_id,
      firstName: m.profiles?.first_name ?? '',
      lastName: m.profiles?.last_name ?? '',
      developerType: m.profiles?.developer_type ?? null,
      role: m.role,
      monthlyRate: m.monthly_rate != null ? parseFloat(m.monthly_rate) : null,
      clientRate: m.client_rate != null ? parseFloat(m.client_rate) : null,
      clientRateType: m.client_rate_type ?? 'hourly',
      allocationPct: m.allocation_pct ?? 100,
      startDate: m.start_date,
      endDate: m.end_date,
    }));

    // Deduplicate by profile_id (keep most recent active record)
    const byProfile = new Map<string, ClientTeamMember>();
    for (const m of all) {
      const existing = byProfile.get(m.profileId);
      if (!existing) {
        byProfile.set(m.profileId, m);
        continue;
      }
      const existingActive =
        !existing.endDate || existing.endDate >= new Date().toISOString().split('T')[0];
      const newActive = !m.endDate || m.endDate >= new Date().toISOString().split('T')[0];
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (m.startDate ?? '') > (existing.startDate ?? ''))
      ) {
        byProfile.set(m.profileId, m);
      }
    }
    return Array.from(byProfile.values());
  },

  async updateMemberRate(
    memberId: string,
    data: {
      monthlyRate: number | null;
      clientRate: number | null;
      clientRateType: 'hourly' | 'monthly';
      role: string | null;
      allocationPct: number;
      startDate: string | null;
      endDate: string | null;
    }
  ): Promise<void> {
    // Get the current member record to check if salary changed
    const { data: member } = await supabase
      .from('team_members')
      .select('profile_id, monthly_rate')
      .eq('id', memberId)
      .single();

    const currentRate = member?.monthly_rate != null ? parseFloat(member.monthly_rate) : null;
    const newRate = data.monthlyRate;
    const salaryChanged = currentRate !== newRate && newRate != null;

    // If salary changed, go through compensation_history (trigger syncs to team_members)
    if (salaryChanged && member?.profile_id) {
      const today = new Date().toISOString().split('T')[0];

      // Close the current compensation record
      const { error: closeErr } = await supabase
        .from('compensation_history')
        .update({ end_date: today })
        .eq('profile_id', member.profile_id)
        .is('end_date', null);
      if (closeErr) throw closeErr;

      // Insert new compensation record
      const { error: insertErr } = await supabase.from('compensation_history').insert({
        profile_id: member.profile_id,
        monthly_payment: newRate,
        hourly_rate: newRate != null ? Math.round((newRate / 160) * 100) / 100 : null,
        start_date: today,
        change_reason: currentRate == null ? 'initial_compensation' : 'adjust',
      });
      if (insertErr) throw insertErr;
      // The trigger sync_compensation_to_team_members will update team_members.monthly_rate
    }

    // Update all fields directly on team_members (don't rely solely on trigger)
    const updates: Record<string, any> = {
      client_rate: data.clientRate,
      client_rate_type: data.clientRateType,
      allocation_pct: data.allocationPct,
      start_date: data.startDate,
      end_date: data.endDate,
      monthly_rate: newRate,
    };
    if (data.role !== undefined) updates.role = data.role;

    const { error } = await supabase.from('team_members').update(updates).eq('id', memberId);

    if (error) throw error;
  },

  async getMonthlyClientRevenue(): Promise<MonthlyClientBreakdown> {
    // Jan–Dec of the current year. Past months = actual data, future months = forecast.
    const orgId = getActiveOrgId();
    const now = new Date();
    const year = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based

    const historicalMonths: string[] = [];
    const forecastMonthsList: string[] = [];

    for (let m = 0; m < 12; m += 1) {
      const key = `${year}-${String(m + 1).padStart(2, '0')}`;
      if (m <= currentMonth) {
        historicalMonths.push(key);
      } else {
        forecastMonthsList.push(key);
      }
    }

    const allMonths = [...historicalMonths, ...forecastMonthsList];

    // Date range for historical queries (Jan 1 – end of current month)
    const histStart = `${year}-01-01`;
    const histEnd = new Date(year, currentMonth + 1, 0).toISOString().split('T')[0];

    // Invoice revenue by team + month (uses billing_period for accrual accuracy)
    let monthlyInvoiceQuery = supabase
      .from('fx_invoices')
      .select('team_id, client_name, amount, date, billing_period')
      .gte('date', histStart)
      .lte('date', histEnd);
    monthlyInvoiceQuery = applyOrgFilter(monthlyInvoiceQuery);
    const { data: invoiceData } = await monthlyInvoiceQuery;

    // Bank transaction revenue (income) with team mapping
    let monthlyTxQuery = supabase
      .from('fx_transactions')
      .select('amount, date, type, fx_vendors!inner(team_id, teams(name))')
      .eq('type', 'income')
      .gte('date', histStart)
      .lte('date', histEnd);
    monthlyTxQuery = applyOrgFilter(monthlyTxQuery);
    const { data: txData } = await monthlyTxQuery;

    // All expense transactions for OpEx. Include the vendor's bulk-processor flag
    // so contractor-payroll debits (Warp/Deel/etc.) can be excluded — those are
    // COGS (captured via team-member rates / actuals), not operating expenses.
    // Fetch ALL expense rows for the period, paginating past PostgREST's default
    // 1000-row cap. Without this the OpEx total silently undercounts once a period
    // has more than 1000 expense transactions (the drill-down is per-month, so it
    // stayed under the cap and looked correct while the breakdown row did not).
    const expenseData: any[] = [];
    for (let from = 0; ; from += 1000) {
      let pageQuery = supabase
        .from('fx_transactions')
        .select('amount, date, fx_categories(name, type), fx_vendors(is_bulk_processor)')
        .eq('type', 'expense')
        .gte('date', histStart)
        .lte('date', histEnd)
        .order('id', { ascending: true })
        .range(from, from + 999);
      pageQuery = applyOrgFilter(pageQuery);
      const { data: page, error: pageErr } = await pageQuery;
      if (pageErr || !page || page.length === 0) break;
      expenseData.push(...page);
      if (page.length < 1000) break;
    }

    // Categories excluded from OpEx (already captured in COGS via team member rates)
    const EXCLUDED_OPEX_CATEGORIES = new Set(['Contractor Payroll', 'Salaries & Wages']);

    // Total revenue & operating expenses from all transactions (historical only)
    const totalRevenue: Record<string, number> = {};
    const totalOpex: Record<string, number> = {};
    const totalCogs: Record<string, number> = {};
    const totalActualCogs: Record<string, number> = {};
    // Operational people = internal team members (not billable to clients).
    const totalOpsPlan: Record<string, number> = {};
    const totalOpsActual: Record<string, number> = {};
    allMonths.forEach((m) => {
      totalRevenue[m] = 0;
      totalOpex[m] = 0;
      totalCogs[m] = 0;
      totalActualCogs[m] = 0;
      totalOpsPlan[m] = 0;
      totalOpsActual[m] = 0;
    });

    // totalRevenue will be computed after per-client revenue is built (see below)

    (expenseData ?? []).forEach((row: any) => {
      const month = row.date?.substring(0, 7);
      if (!month || !(month in totalOpex)) return;
      // Transfers / owner draws / credit-card payments are money movement, not expenses.
      if ((row as any).fx_categories?.type === 'transfer') return;
      // Contractor payroll paid via a bulk processor is COGS, not OpEx.
      if ((row as any).fx_vendors?.is_bulk_processor === true) return;
      const categoryName = (row as any).fx_categories?.name ?? '';
      if (!EXCLUDED_OPEX_CATEGORIES.has(categoryName)) {
        totalOpex[month] += parseFloat(row.amount);
      }
    });

    // Get all teams linked to vendors (clients)
    let monthlyVendorsQuery = supabase
      .from('fx_vendors')
      .select('id, team_id, teams(id, name)')
      .not('team_id', 'is', null);
    monthlyVendorsQuery = applyOrgFilter(monthlyVendorsQuery);
    const { data: vendorsWithTeams } = await monthlyVendorsQuery;

    const teamNames = new Map<string, string>();
    for (const v of vendorsWithTeams ?? []) {
      const team = (v as any).teams;
      if (team) teamNames.set(team.id, team.name);
    }

    // Internal teams = operational people (ops/admin/recruiting/etc.), not billable.
    let internalTeamsQuery = supabase
      .from('teams')
      .select('id, name, area')
      .eq('category', 'internal');
    internalTeamsQuery = applyOrgFilter(internalTeamsQuery);
    const { data: internalTeams } = await internalTeamsQuery;
    const internalTeamIds = (internalTeams ?? []).map((t: any) => t.id);

    const { data: internalMembers } = await supabase
      .from('team_members')
      .select('profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date')
      .in('team_id', internalTeamIds.length > 0 ? internalTeamIds : ['__none__']);

    // Dedupe internal members by (profile_id, team_id) — prefer active, then latest start.
    const internalDeduped = new Map<string, any>();
    for (const member of internalMembers ?? []) {
      const mem = member as any;
      const key = `${mem.profile_id}::${mem.team_id}`;
      const existing = internalDeduped.get(key);
      if (!existing) {
        internalDeduped.set(key, mem);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !mem.end_date || mem.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (mem.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        internalDeduped.set(key, mem);
      }
    }

    // Plan cost per month = rate × allocation for internal members active that month.
    // Also collect each internal person's allocation fraction for the actual (payroll) split.
    const internalAllocByProfile = new Map<string, number>();
    for (const member of internalDeduped.values()) {
      const m = member as any;
      const monthlyRate = m.monthly_rate != null ? parseFloat(m.monthly_rate) : null;
      const allocPct = (m.allocation_pct ?? 100) / 100;
      if (m.profile_id) {
        internalAllocByProfile.set(
          m.profile_id,
          (internalAllocByProfile.get(m.profile_id) ?? 0) + allocPct
        );
      }
      if (monthlyRate == null) continue;
      const memberStart = m.start_date ? parseLocalDate(m.start_date) : parseLocalDate('2020-01-01');
      const memberEnd = m.end_date ? parseLocalDate(m.end_date) : parseLocalDate('2099-12-31');
      const monthlyCost = monthlyRate * allocPct;
      for (const month of allMonths) {
        const [y, mo] = month.split('-').map(Number);
        const monthStart = new Date(y, mo - 1, 1);
        const monthEnd = new Date(y, mo, 0);
        if (monthEnd < memberStart || monthStart > memberEnd) continue;
        totalOpsPlan[month] += monthlyCost;
      }
    }

    // Get team members for COGS and forecast
    const teamIds = Array.from(teamNames.keys());
    const { data: membersData } = await supabase
      .from('team_members')
      .select(
        'profile_id, team_id, monthly_rate, client_rate, client_rate_type, allocation_pct, start_date, end_date'
      )
      .in('team_id', teamIds.length > 0 ? teamIds : ['__none__']);

    // Build client data map
    type ClientEntry = {
      teamId: string;
      clientName: string;
      monthlyRevenue: Record<string, number>;
      monthlyCogs: Record<string, number>;
      monthlyActualCogs: Record<string, number>;
    };
    const clientMap = new Map<string, ClientEntry>();

    function ensureClient(teamId: string, name: string): ClientEntry {
      if (!clientMap.has(teamId)) {
        const rev: Record<string, number> = {};
        const cogs: Record<string, number> = {};
        const actualCogs: Record<string, number> = {};
        allMonths.forEach((m) => {
          rev[m] = 0;
          cogs[m] = 0;
          actualCogs[m] = 0;
        });
        clientMap.set(teamId, {
          teamId,
          clientName: name,
          monthlyRevenue: rev,
          monthlyCogs: cogs,
          monthlyActualCogs: actualCogs,
        });
      }
      return clientMap.get(teamId) as ClientEntry;
    }

    // Historical revenue — invoices first (use billing_period when set)
    const teamMonthsWithInvoices = new Set<string>();

    (invoiceData ?? []).forEach((row: any) => {
      if (!row.team_id) return;
      const month = row.billing_period ?? row.date?.substring(0, 7);
      if (!month) return;
      teamMonthsWithInvoices.add(`${row.team_id}::${month}`);
      const name = row.client_name || teamNames.get(row.team_id) || row.team_id;
      const entry = ensureClient(row.team_id, name);
      if (month in entry.monthlyRevenue) {
        entry.monthlyRevenue[month] += parseFloat(row.amount);
      }
    });

    // Fallback: bank income transactions for team-months without invoices
    (txData ?? []).forEach((row: any) => {
      if (row.type !== 'income') return;
      const teamId = row.fx_vendors?.team_id;
      if (!teamId) return;
      const teamName = (row.fx_vendors as any)?.teams?.name;
      if (!teamName) return;
      const month = row.date?.substring(0, 7);
      if (!month) return;
      if (teamMonthsWithInvoices.has(`${teamId}::${month}`)) return;
      const entry = ensureClient(teamId, teamName);
      if (month in entry.monthlyRevenue) {
        entry.monthlyRevenue[month] += parseFloat(row.amount);
      }
    });

    // Calculate COGS per client per month + forecast revenue & COGS
    // Deduplicate by (profile_id, team_id) to prevent double-counting
    const monthlyDeduped = new Map<string, any>();
    for (const member of membersData ?? []) {
      const mem = member as any;
      const key = `${mem.profile_id}::${mem.team_id}`;
      const existing = monthlyDeduped.get(key);
      if (!existing) {
        monthlyDeduped.set(key, mem);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !mem.end_date || mem.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (mem.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        monthlyDeduped.set(key, mem);
      }
    }

    for (const member of monthlyDeduped.values()) {
      const m = member as any;
      const teamId = m.team_id;
      const teamName = teamNames.get(teamId);
      if (!teamName) continue;

      const entry = ensureClient(teamId, teamName);
      const monthlyRate = m.monthly_rate != null ? parseFloat(m.monthly_rate) : null;
      const clientRate = m.client_rate != null ? parseFloat(m.client_rate) : null;
      const isHourly = m.client_rate_type === 'hourly';
      const allocPct = (m.allocation_pct ?? 100) / 100;
      const memberStart = m.start_date
        ? parseLocalDate(m.start_date)
        : parseLocalDate('2020-01-01');
      const memberEnd = m.end_date ? parseLocalDate(m.end_date) : parseLocalDate('2099-12-31');

      const monthlyClientRevenue =
        clientRate != null ? (isHourly ? clientRate * 160 : clientRate) * allocPct : null;
      const monthlyCost = monthlyRate != null ? monthlyRate * allocPct : null;

      for (const month of allMonths) {
        const [y, mo] = month.split('-').map(Number);
        const monthStart = new Date(y, mo - 1, 1);
        const monthEnd = new Date(y, mo, 0);

        // Check if member active during this month
        if (monthEnd < memberStart || monthStart > memberEnd) continue;

        const isForecast = forecastMonthsList.includes(month);

        // COGS: uses monthly_rate for both historical and forecast
        if (monthlyCost != null) {
          entry.monthlyCogs[month] += monthlyCost;
          totalCogs[month] += monthlyCost;
        }

        // Forecast revenue from client rates
        if (isForecast && monthlyClientRevenue != null) {
          entry.monthlyRevenue[month] += monthlyClientRevenue;
        }
      }
    }

    // Actual contractor COGS from Warp payroll (fx_bulk_reports / items).
    // Total = full USD payroll spend per month; per-client = allocation-weighted
    // (comparable to the rate-based plan COGS above).
    const allocByProfile = new Map<string, Array<{ teamId: string; pct: number }>>();
    for (const member of monthlyDeduped.values()) {
      const m = member as any;
      if (!m.profile_id || !m.team_id || !teamNames.has(m.team_id)) continue;
      const arr = allocByProfile.get(m.profile_id) ?? [];
      arr.push({ teamId: m.team_id, pct: (m.allocation_pct ?? 100) / 100 });
      allocByProfile.set(m.profile_id, arr);
    }

    let bulkItemsQuery = supabase
      .from('fx_bulk_report_items')
      .select('gross_amount, exchange_rate, profile_id, fx_bulk_reports!inner(period_start, organization_id)')
      .gte('fx_bulk_reports.period_start', histStart)
      .lte('fx_bulk_reports.period_start', histEnd);
    if (orgId) bulkItemsQuery = bulkItemsQuery.eq('fx_bulk_reports.organization_id', orgId);
    const { data: bulkItems } = await bulkItemsQuery;

    for (const item of bulkItems ?? []) {
      const row = item as any;
      const rate = row.exchange_rate != null ? parseFloat(row.exchange_rate) : 1;
      const usd = (parseFloat(row.gross_amount) || 0) * (rate || 1);
      const month = row.fx_bulk_reports?.period_start?.substring(0, 7);
      if (!month || !(month in totalActualCogs)) continue;

      // Total actual = full payroll spend (what you actually pay contractors)
      totalActualCogs[month] += usd;

      // Per-client actual = allocation-weighted share (matches plan COGS methodology)
      const allocs = row.profile_id ? allocByProfile.get(row.profile_id) : null;
      if (allocs) {
        for (const a of allocs) {
          const teamName = teamNames.get(a.teamId);
          if (!teamName) continue;
          ensureClient(a.teamId, teamName).monthlyActualCogs[month] += usd * a.pct;
        }
      }

      // Operational people actual = the internal-allocated share of this person's pay.
      const internalFrac = row.profile_id ? internalAllocByProfile.get(row.profile_id) : null;
      if (internalFrac) totalOpsActual[month] += usd * Math.min(internalFrac, 1);
    }

    // Build totalRevenue by summing per-client revenue (consistent data source)
    for (const month of allMonths) {
      let monthTotal = 0;
      for (const entry of clientMap.values()) {
        monthTotal += entry.monthlyRevenue[month];
      }
      totalRevenue[month] = monthTotal;
    }

    // Project future OpEx using trailing average of historical months with data
    const historicalOpexMonths = historicalMonths.filter((m) => totalOpex[m] > 0);
    if (historicalOpexMonths.length > 0) {
      const avgOpex =
        historicalOpexMonths.reduce((s, m) => s + totalOpex[m], 0) / historicalOpexMonths.length;
      for (const month of forecastMonthsList) {
        totalOpex[month] = Math.round(avgOpex * 100) / 100;
      }
    }

    const clients = Array.from(clientMap.values())
      .filter((c) => {
        const totalRev = Object.values(c.monthlyRevenue).reduce((s, v) => s + v, 0);
        const totalCog = Object.values(c.monthlyCogs).reduce((s, v) => s + v, 0);
        const totalActual = Object.values(c.monthlyActualCogs).reduce((s, v) => s + v, 0);
        return totalRev > 0 || totalCog > 0 || totalActual > 0;
      })
      .sort((a, b) => {
        const aTotal = Object.values(a.monthlyRevenue).reduce((s, v) => s + v, 0);
        const bTotal = Object.values(b.monthlyRevenue).reduce((s, v) => s + v, 0);
        return bTotal - aTotal;
      });

    return {
      months: allMonths,
      forecastStartMonth: forecastMonthsList[0] ?? null,
      totalRevenue,
      totalCogs,
      totalActualCogs,
      totalOpsPlan,
      totalOpsActual,
      totalOpex,
      clients,
    };
  },

  async getRevenueDrillDown(month: string): Promise<DrillDownRow[]> {
    const startDate = `${month}-01`;
    const endDate = new Date(
      parseInt(month.split('-')[0], 10),
      parseInt(month.split('-')[1], 10),
      0
    )
      .toISOString()
      .split('T')[0];

    // Mirror getMonthlyClientRevenue: invoices (attributed by billing_period ?? date)
    // are the primary source; bank income fills team-months without invoices.

    // 1. Invoices belonging to this month (billing_period, or date when unset)
    let invQuery = supabase
      .from('fx_invoices')
      .select('id, invoice_number, client_name, team_id, amount, date, billing_period')
      .or(
        `billing_period.eq.${month},and(billing_period.is.null,date.gte.${startDate},date.lte.${endDate})`
      );
    invQuery = applyOrgFilter(invQuery);
    const { data: invoices } = await invQuery;

    const rows: DrillDownRow[] = [];
    const teamsWithInvoice = new Set<string>();
    (invoices ?? []).forEach((inv: any) => {
      const monthOf = inv.billing_period ?? inv.date?.substring(0, 7);
      if (monthOf !== month) return;
      if (inv.team_id) teamsWithInvoice.add(inv.team_id);
      rows.push({
        id: inv.id,
        date: inv.date,
        description: inv.invoice_number ? `Invoice ${inv.invoice_number}` : inv.client_name || 'Invoice',
        amount: parseFloat(inv.amount) || 0,
        vendor: inv.client_name || '',
        category: 'Invoice',
      });
    });

    // 2. Bank income — only for teams that had no invoice this month (fallback)
    let revDrillQuery = supabase
      .from('fx_transactions')
      .select('id, date, description, amount, type, fx_vendors!inner(team_id, name, teams(name))')
      .eq('type', 'income')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date');
    revDrillQuery = applyOrgFilter(revDrillQuery);
    const { data: transactions } = await revDrillQuery;

    (transactions ?? []).forEach((tx: any) => {
      const teamId = (tx.fx_vendors as any)?.team_id;
      if (teamId && teamsWithInvoice.has(teamId)) return;
      rows.push({
        id: tx.id,
        date: tx.date,
        description: tx.description || 'Income',
        amount: parseFloat(tx.amount) || 0,
        vendor: (tx.fx_vendors as any)?.name || '',
        category: (tx.fx_vendors as any)?.teams?.name || 'Income',
      });
    });

    return rows.sort((a, b) => (a.date < b.date ? -1 : 1));
  },

  async getRevenueForecastDrillDown(month: string): Promise<RevenueForecastDrillDownRow[]> {
    const [yearStr, moStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    const mo = parseInt(moStr, 10);
    const monthStart = new Date(year, mo - 1, 1);
    const monthEnd = new Date(year, mo, 0);

    let vendorsQuery = supabase
      .from('fx_vendors')
      .select('id, team_id, teams(id, name)')
      .not('team_id', 'is', null);
    vendorsQuery = applyOrgFilter(vendorsQuery);
    const { data: vendorsWithTeams } = await vendorsQuery;

    const teamNames = new Map<string, string>();
    for (const v of vendorsWithTeams ?? []) {
      const team = (v as any).teams;
      if (team) teamNames.set(team.id, team.name);
    }

    const teamIds = Array.from(teamNames.keys());
    const { data: membersData } = await supabase
      .from('team_members')
      .select(
        'id, profile_id, team_id, client_rate, client_rate_type, allocation_pct, start_date, end_date, profiles(first_name, last_name)'
      )
      .in('team_id', teamIds.length > 0 ? teamIds : ['__none__']);

    // Deduplicate by (profile_id, team_id)
    const deduped = new Map<string, any>();
    for (const member of membersData ?? []) {
      const mem = member as any;
      const key = `${mem.profile_id}::${mem.team_id}`;
      const existing = deduped.get(key);
      if (!existing) {
        deduped.set(key, mem);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !mem.end_date || mem.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (mem.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        deduped.set(key, mem);
      }
    }

    const rows: RevenueForecastDrillDownRow[] = [];

    for (const member of deduped.values()) {
      const clientRate = member.client_rate != null ? parseFloat(member.client_rate) : null;
      if (clientRate == null) continue;

      const memberStart = member.start_date
        ? parseLocalDate(member.start_date)
        : parseLocalDate('2020-01-01');
      const memberEnd = member.end_date
        ? parseLocalDate(member.end_date)
        : parseLocalDate('2099-12-31');
      if (monthEnd < memberStart || monthStart > memberEnd) continue;

      const allocPct = member.allocation_pct ?? 100;
      const isHourly = member.client_rate_type === 'hourly';
      const projectedRevenue = (isHourly ? clientRate * 160 : clientRate) * (allocPct / 100);
      const firstName = member.profiles?.first_name || '';
      const lastName = member.profiles?.last_name || '';

      rows.push({
        id: member.id,
        memberName: `${firstName} ${lastName}`.trim() || 'Unknown',
        teamName: teamNames.get(member.team_id) || 'Unknown',
        clientRate,
        clientRateType: isHourly ? 'hourly' : 'monthly',
        allocationPct: allocPct,
        projectedRevenue,
      });
    }

    return rows.sort((a, b) => b.projectedRevenue - a.projectedRevenue);
  },

  async getCogsDrillDown(month: string): Promise<CogsDrillDownRow[]> {
    const [yearStr, moStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    const mo = parseInt(moStr, 10);
    const monthStart = new Date(year, mo - 1, 1);
    const monthEnd = new Date(year, mo, 0);
    const monthStartIso = `${month}-01`;
    const monthEndIso = monthEnd.toLocaleDateString('en-CA');

    let cogsVendorsQuery = supabase
      .from('fx_vendors')
      .select('id, team_id, teams(id, name)')
      .not('team_id', 'is', null);
    cogsVendorsQuery = applyOrgFilter(cogsVendorsQuery);
    const { data: vendorsWithTeams } = await cogsVendorsQuery;

    const teamNames = new Map<string, string>();
    for (const v of vendorsWithTeams ?? []) {
      const team = (v as any).teams;
      if (team) teamNames.set(team.id, team.name);
    }

    const teamIds = Array.from(teamNames.keys());
    const { data: membersData } = await supabase
      .from('team_members')
      .select(
        'id, profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date, role, profiles(first_name, last_name)'
      )
      .in('team_id', teamIds.length > 0 ? teamIds : ['__none__']);

    // Deduplicate by (profile_id, team_id) — keep latest active record
    const deduped = new Map<string, any>();
    for (const m of membersData ?? []) {
      const member = m as any;
      const key = `${member.profile_id}::${member.team_id}`;
      const existing = deduped.get(key);
      if (!existing) {
        deduped.set(key, member);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !member.end_date || member.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (member.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        deduped.set(key, member);
      }
    }

    // ACTUAL basis first. The Monthly Breakdown shows measured payroll whenever a
    // report exists for the month (effectiveCogs), so the drill-down has to do the
    // same — otherwise clicking $80,007 opened a list totalling $108,345, because
    // the modal was always computing the rate-card plan.
    let payrollQuery = supabase
      .from('fx_bulk_report_items')
      .select(
        'id, first_name, last_name, profile_id, gross_amount, exchange_rate, fx_bulk_reports!inner(period_start, organization_id)'
      )
      .gte('fx_bulk_reports.period_start', monthStartIso)
      .lte('fx_bulk_reports.period_start', monthEndIso);
    const orgId = getActiveOrgId();
    if (orgId) payrollQuery = payrollQuery.eq('fx_bulk_reports.organization_id', orgId);
    const { data: payrollItems } = await payrollQuery;

    if ((payrollItems ?? []).length > 0) {
      // Which client team each person is allocated to, for the Team column.
      const teamByProfile = new Map<string, string>();
      const roleByProfile = new Map<string, string>();
      for (const member of deduped.values()) {
        if (!member.profile_id) continue;
        teamByProfile.set(member.profile_id, teamNames.get(member.team_id) || 'Unknown');
        if (member.role) roleByProfile.set(member.profile_id, member.role);
      }

      return (payrollItems ?? [])
        .map((item: any) => {
          const rate = item.exchange_rate != null ? parseFloat(item.exchange_rate) : 1;
          const cost = (parseFloat(item.gross_amount) || 0) * (rate || 1);
          return {
            id: item.id,
            memberName: `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() || 'Unknown',
            // No client allocation means this person's pay reaches no client —
            // worth seeing rather than hiding behind a blank.
            teamName: item.profile_id
              ? teamByProfile.get(item.profile_id) ?? 'Not allocated to a client'
              : 'Not linked to a profile',
            role: item.profile_id ? roleByProfile.get(item.profile_id) ?? '' : '',
            monthlyRate: cost,
            allocationPct: 100,
            cost,
            basis: 'actual' as const,
          };
        })
        .sort((a, b) => b.cost - a.cost);
    }

    // PLAN basis — no payroll uploaded for this month, so fall back to rate cards.
    const rows: CogsDrillDownRow[] = [];

    for (const member of deduped.values()) {
      const monthlyRate = member.monthly_rate != null ? parseFloat(member.monthly_rate) : null;
      if (monthlyRate == null) continue;

      const memberStart = member.start_date
        ? parseLocalDate(member.start_date)
        : parseLocalDate('2020-01-01');
      const memberEnd = member.end_date
        ? parseLocalDate(member.end_date)
        : parseLocalDate('2099-12-31');
      if (monthEnd < memberStart || monthStart > memberEnd) continue;

      const allocPct = member.allocation_pct ?? 100;
      const cost = monthlyRate * (allocPct / 100);
      const firstName = member.profiles?.first_name || '';
      const lastName = member.profiles?.last_name || '';

      rows.push({
        id: member.id,
        memberName: `${firstName} ${lastName}`.trim() || 'Unknown',
        teamName: teamNames.get(member.team_id) || 'Unknown',
        role: member.role || '',
        monthlyRate,
        allocationPct: allocPct,
        cost,
        basis: 'plan',
      });
    }

    return rows.sort((a, b) => b.cost - a.cost);
  },

  // Operational people (internal team members) active in the month, with plan cost.
  async getOpsPeopleDrillDown(month: string): Promise<CogsDrillDownRow[]> {
    const [yearStr, moStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    const mo = parseInt(moStr, 10);
    const monthStart = new Date(year, mo - 1, 1);
    const monthEnd = new Date(year, mo, 0);

    let internalTeamsQuery = supabase
      .from('teams')
      .select('id, name')
      .eq('category', 'internal');
    internalTeamsQuery = applyOrgFilter(internalTeamsQuery);
    const { data: internalTeams } = await internalTeamsQuery;

    const teamNames = new Map<string, string>();
    for (const t of internalTeams ?? []) teamNames.set((t as any).id, (t as any).name);
    const teamIds = Array.from(teamNames.keys());

    const { data: membersData } = await supabase
      .from('team_members')
      .select(
        'id, profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date, role, profiles(first_name, last_name)'
      )
      .in('team_id', teamIds.length > 0 ? teamIds : ['__none__']);

    const deduped = new Map<string, any>();
    for (const m of membersData ?? []) {
      const member = m as any;
      const key = `${member.profile_id}::${member.team_id}`;
      const existing = deduped.get(key);
      if (!existing) {
        deduped.set(key, member);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !member.end_date || member.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (member.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        deduped.set(key, member);
      }
    }

    const profileTeam = new Map<string, string>();
    for (const member of deduped.values()) {
      if (member.profile_id) profileTeam.set(member.profile_id, member.team_id);
    }

    // ACTUAL basis first, mirroring effectiveOps on the dashboard: measured
    // payroll where a report exists, weighted by each person's internal
    // allocation, because someone 40% internal only carries 40% of their pay
    // as operational cost.
    const internalFracByProfile = new Map<string, number>();
    for (const member of deduped.values()) {
      if (!member.profile_id) continue;
      const frac = (member.allocation_pct ?? 100) / 100;
      internalFracByProfile.set(
        member.profile_id,
        (internalFracByProfile.get(member.profile_id) ?? 0) + frac
      );
    }

    const monthStartIso = `${month}-01`;
    const monthEndIso = monthEnd.toLocaleDateString('en-CA');
    let opsPayrollQuery = supabase
      .from('fx_bulk_report_items')
      .select(
        'id, first_name, last_name, profile_id, gross_amount, exchange_rate, fx_bulk_reports!inner(period_start, organization_id)'
      )
      .not('profile_id', 'is', null)
      .gte('fx_bulk_reports.period_start', monthStartIso)
      .lte('fx_bulk_reports.period_start', monthEndIso);
    const opsOrgId = getActiveOrgId();
    if (opsOrgId) opsPayrollQuery = opsPayrollQuery.eq('fx_bulk_reports.organization_id', opsOrgId);
    const { data: opsPayrollItems } = await opsPayrollQuery;

    const actualOpsRows = (opsPayrollItems ?? [])
      .filter((item: any) => internalFracByProfile.has(item.profile_id))
      .map((item: any) => {
        const rate = item.exchange_rate != null ? parseFloat(item.exchange_rate) : 1;
        const paid = (parseFloat(item.gross_amount) || 0) * (rate || 1);
        const frac = Math.min(internalFracByProfile.get(item.profile_id) ?? 0, 1);
        return {
          id: item.id,
          memberName: `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() || 'Unknown',
          teamName: teamNames.get(profileTeam.get(item.profile_id) ?? '') || 'Internal',
          role: '',
          monthlyRate: paid,
          allocationPct: Math.round(frac * 100),
          cost: paid * frac,
          basis: 'actual' as const,
        };
      });

    if (actualOpsRows.length > 0) return actualOpsRows.sort((a, b) => b.cost - a.cost);

    // PLAN basis — no payroll for this month, fall back to rate cards.
    const rows: CogsDrillDownRow[] = [];
    for (const member of deduped.values()) {
      const monthlyRate = member.monthly_rate != null ? parseFloat(member.monthly_rate) : null;
      if (monthlyRate == null) continue;

      const memberStart = member.start_date
        ? parseLocalDate(member.start_date)
        : parseLocalDate('2020-01-01');
      const memberEnd = member.end_date
        ? parseLocalDate(member.end_date)
        : parseLocalDate('2099-12-31');
      if (monthEnd < memberStart || monthStart > memberEnd) continue;

      const allocPct = member.allocation_pct ?? 100;
      const firstName = member.profiles?.first_name || '';
      const lastName = member.profiles?.last_name || '';
      rows.push({
        id: member.id,
        memberName: `${firstName} ${lastName}`.trim() || 'Unknown',
        teamName: teamNames.get(member.team_id) || 'Unknown',
        role: member.role || '',
        monthlyRate,
        allocationPct: allocPct,
        cost: monthlyRate * (allocPct / 100),
        basis: 'plan',
      });
    }
    return rows.sort((a, b) => b.cost - a.cost);
  },

  async getOpexDrillDown(month: string): Promise<DrillDownRow[]> {
    const startDate = `${month}-01`;
    const endDate = new Date(
      parseInt(month.split('-')[0], 10),
      parseInt(month.split('-')[1], 10),
      0
    )
      .toISOString()
      .split('T')[0];

    const EXCLUDED = ['Contractor Payroll', 'Salaries & Wages'];

    let opexQuery = supabase
      .from('fx_transactions')
      .select('id, date, description, amount, fx_vendors(name, is_bulk_processor), fx_categories(name, type)')
      .eq('type', 'expense')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date');
    opexQuery = applyOrgFilter(opexQuery);
    const { data } = await opexQuery;

    return (data ?? [])
      .filter((tx: any) => {
        // Exclude transfers / owner draws — money movement, not OpEx.
        if ((tx as any).fx_categories?.type === 'transfer') return false;
        // Exclude bulk-processor (contractor payroll) — it's COGS, not OpEx.
        if ((tx as any).fx_vendors?.is_bulk_processor === true) return false;
        const catName = (tx as any).fx_categories?.name ?? '';
        return !EXCLUDED.includes(catName);
      })
      .map((tx: any) => ({
        id: tx.id,
        date: tx.date,
        description: tx.description || 'Expense',
        amount: parseFloat(tx.amount),
        vendor: (tx.fx_vendors as any)?.name || '',
        category: (tx.fx_categories as any)?.name || '',
      }));
  },

  /**
   * Invoiced revenue that reaches no client, because the invoice has no team_id.
   *
   * This is the silent failure mode of the whole report: a client whose
   * QuickBooks customer name differs from its Zion team name gets its revenue
   * dropped, and the client card then shows cost with no income and looks
   * unprofitable. It happened three times before anyone noticed — VolFront→VolAI
   * ($10,488), LexisNexis→Flyreel ($59,504) and Thrifty Traveler ($8,100). No
   * name-similarity match would have caught the first two.
   *
   * Surfacing the number means the next occurrence is visible immediately
   * instead of quietly making a client look like it is losing money.
   */
  async getUnattributedRevenue({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<{ total: number; clients: Array<{ clientName: string; amount: number; invoices: number }> }> {
    let query = supabase
      .from('fx_invoices')
      .select('client_name, amount')
      .is('team_id', null)
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;

    const byClient = new Map<string, { clientName: string; amount: number; invoices: number }>();
    let total = 0;
    for (const row of data ?? []) {
      const amount = parseFloat((row as any).amount) || 0;
      const name = (row as any).client_name || 'Unknown';
      total += amount;
      const entry = byClient.get(name) ?? { clientName: name, amount: 0, invoices: 0 };
      entry.amount += amount;
      entry.invoices += 1;
      byClient.set(name, entry);
    }

    return {
      total: Math.round(total * 100) / 100,
      clients: Array.from(byClient.values()).sort((a, b) => b.amount - a.amount),
    };
  },

  async getAllTeams(): Promise<
    Array<{ id: string; name: string; category: string | null; memberCount: number }>
  > {
    let teamsQuery = supabase
      .from('teams')
      .select('id, name, category, team_members(id)')
      .order('name');
    teamsQuery = applyOrgFilter(teamsQuery);
    const { data, error } = await teamsQuery;

    if (error) throw error;

    return (data ?? []).map((t: any) => ({
      id: t.id,
      name: t.name,
      category: t.category,
      memberCount: t.team_members?.length ?? 0,
    }));
  },

  async createClient(name: string): Promise<{ teamId: string; vendorId: string }> {
    const orgId = getActiveOrgId();

    // Create team (category = 'external' for clients)
    const { data: team, error: teamErr } = await supabase
      .from('teams')
      .insert({
        name,
        category: 'external',
        ...(orgId ? { organization_id: orgId } : {}),
      })
      .select('id')
      .single();
    if (teamErr) throw teamErr;

    // Create matching vendor linked to the team
    const { data: vendor, error: vendorErr } = await supabase
      .from('fx_vendors')
      .insert({
        name,
        display_name: name,
        team_id: team.id,
        is_active: true,
        ...(orgId ? { organization_id: orgId } : {}),
      })
      .select('id')
      .single();
    if (vendorErr) throw vendorErr;

    return { teamId: team.id, vendorId: vendor.id };
  },

  async getAvailableProfiles(): Promise<
    Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      developerType: string | null;
    }>
  > {
    let profilesQuery = supabase
      .from('profiles')
      .select('id, first_name, last_name, email, developer_type')
      .is('deleted_at', null)
      .order('first_name');
    profilesQuery = applyOrgFilter(profilesQuery);
    const { data, error } = await profilesQuery;

    if (error) throw error;

    return (data ?? []).map((p: any) => ({
      id: p.id,
      firstName: p.first_name,
      lastName: p.last_name,
      email: p.email,
      developerType: p.developer_type,
    }));
  },

  /**
   * @param opts.staffOnly Restrict to people who could plausibly be on payroll
   *   (staff type, Warp roster, team allocation, vendor link, or prior payroll
   *   row) — 123 profiles rather than 23k of mostly recruiting candidates.
   *   Off by default: the "Add Member" pickers must still reach new hires.
   */
  async searchProfiles(
    query: string,
    opts: { staffOnly?: boolean } = {}
  ): Promise<
    Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      developerType: string | null;
    }>
  > {
    if (!query || query.length < 2) return [];

    // Filtering AND ranking happen in Postgres (see the search_profiles migration).
    // Doing it client-side meant the row cap truncated alphabetically before the
    // full-name token filter ran, so real matches never reached the dropdown —
    // "Lucas" matches 544 of 23k profiles, and "Lucas Bittencourt" fell outside
    // the first 100 rows.
    const { data, error } = await supabase.rpc('search_profiles', {
      q: query.trim(),
      max_results: 100,
      staff_only: opts.staffOnly ?? false,
    });

    if (error) throw error;

    return (data ?? []).map((p: any) => ({
      id: p.id,
      firstName: p.first_name,
      lastName: p.last_name,
      email: p.email,
      developerType: p.developer_type,
    }));
  },

  async addMemberToClient(
    teamId: string,
    profileId: string,
    data: { role?: string; monthlyRate?: number; allocationPct?: number }
  ): Promise<void> {
    // Guard against duplicate active allocations for same person + team
    const today = new Date().toISOString().split('T')[0];
    const { data: existing } = await supabase
      .from('team_members')
      .select('id')
      .eq('profile_id', profileId)
      .eq('team_id', teamId)
      .or(`end_date.is.null,end_date.gte.${today}`)
      .limit(1);

    if (existing && existing.length > 0) {
      throw new Error('This person is already a member of this team.');
    }

    const { error } = await supabase.from('team_members').insert({
      team_id: teamId,
      profile_id: profileId,
      role: data.role ?? 'member',
      monthly_rate: data.monthlyRate ?? null,
      allocation_pct: data.allocationPct ?? 100,
    });
    if (error) throw error;
  },

  async removeMemberFromClient(teamId: string, profileId: string): Promise<void> {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('profile_id', profileId);
    if (error) throw error;
  },

  async getMonthlyPnL(
    startDate: string,
    endDate: string,
    savingsTargetPct = 10
  ): Promise<MonthlyPnLData> {
    const now = new Date();
    const todayMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Build list of months in the date range
    const startParts = startDate.split('-').map(Number);
    const endParts = endDate.split('-').map(Number);
    const allMonths: string[] = [];
    let y = startParts[0];
    let m = startParts[1];
    const endKey = `${endParts[0]}-${String(endParts[1]).padStart(2, '0')}`;
    let key = `${y}-${String(m).padStart(2, '0')}`;
    while (key <= endKey) {
      allMonths.push(key);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      key = `${y}-${String(m).padStart(2, '0')}`;
    }

    // 1. Invoice revenue by month (uses billing_period for accrual accuracy, falls back to date)
    let invoiceQuery = supabase
      .from('fx_invoices')
      .select('amount, date, billing_period')
      .gte('date', startDate)
      .lte('date', endDate);
    invoiceQuery = applyOrgFilter(invoiceQuery);

    // 3. Teams + members for COGS
    let vendorsQuery = supabase
      .from('fx_vendors')
      .select('id, team_id, teams(id, name)')
      .not('team_id', 'is', null);
    vendorsQuery = applyOrgFilter(vendorsQuery);

    const [invoiceResult, vendorsResult] = await Promise.all([invoiceQuery, vendorsQuery]);

    // 2. Expense transactions for OpEx — paginate past PostgREST's 1000-row cap
    // (otherwise OpEx silently undercounts once a range has >1000 expense rows).
    const expenseData: any[] = [];
    for (let from = 0; ; from += 1000) {
      let pageQuery = supabase
        .from('fx_transactions')
        .select('amount, date, fx_categories(name, type)')
        .eq('type', 'expense')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('id', { ascending: true })
        .range(from, from + 999);
      pageQuery = applyOrgFilter(pageQuery);
      const { data: page, error: pageErr } = await pageQuery;
      if (pageErr || !page || page.length === 0) break;
      expenseData.push(...page);
      if (page.length < 1000) break;
    }

    const invoiceData = invoiceResult.data ?? [];
    const vendorsWithTeams = vendorsResult.data ?? [];

    const teamIds = vendorsWithTeams.map((v: any) => v.team_id).filter(Boolean);
    const { data: membersData } = await supabase
      .from('team_members')
      .select('profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date')
      .in('team_id', teamIds.length > 0 ? teamIds : ['__none__']);

    // Initialize monthly buckets
    const revenue: Record<string, number> = {};
    const cogs: Record<string, number> = {};
    const opex: Record<string, number> = {};
    allMonths.forEach((mo) => {
      revenue[mo] = 0;
      cogs[mo] = 0;
      opex[mo] = 0;
    });

    // Revenue from invoices — use billing_period (accrual) when set, fall back to invoice date
    invoiceData.forEach((row: any) => {
      const month = row.billing_period ?? row.date?.substring(0, 7);
      if (month && month in revenue) {
        revenue[month] += parseFloat(row.amount);
      }
    });

    // Split expense transactions: payroll → COGS (actual), everything else → OpEx
    const PAYROLL_CATEGORIES = new Set(['Contractor Payroll', 'Salaries & Wages']);
    const actualPayroll: Record<string, number> = {};
    allMonths.forEach((mo) => {
      actualPayroll[mo] = 0;
    });

    expenseData.forEach((row: any) => {
      const month = row.date?.substring(0, 7);
      if (!month || !(month in opex)) return;
      // Transfers / owner draws are money movement, not expenses.
      if ((row as any).fx_categories?.type === 'transfer') return;
      const catName = (row as any).fx_categories?.name ?? '';
      if (PAYROLL_CATEGORIES.has(catName)) {
        actualPayroll[month] += parseFloat(row.amount);
      } else {
        opex[month] += parseFloat(row.amount);
      }
    });

    // COGS: past months use actual payroll from bank, future uses team member rates
    // Rates-based COGS (needed for future months + fallback when no payroll data)
    const ratesCogs: Record<string, number> = {};
    allMonths.forEach((mo) => {
      ratesCogs[mo] = 0;
    });

    const pnlDeduped = new Map<string, any>();
    for (const member of membersData ?? []) {
      const mem = member as any;
      const dedupKey = `${mem.profile_id}::${mem.team_id}`;
      const existing = pnlDeduped.get(dedupKey);
      if (!existing) {
        pnlDeduped.set(dedupKey, mem);
        continue;
      }
      const today = new Date().toISOString().split('T')[0];
      const existingActive = !existing.end_date || existing.end_date >= today;
      const newActive = !mem.end_date || mem.end_date >= today;
      if (
        (!existingActive && newActive) ||
        (existingActive === newActive && (mem.start_date ?? '') > (existing.start_date ?? ''))
      ) {
        pnlDeduped.set(dedupKey, mem);
      }
    }

    for (const mem of pnlDeduped.values()) {
      const monthlyRate = mem.monthly_rate != null ? parseFloat(mem.monthly_rate) : null;
      if (monthlyRate == null) continue;

      const allocPct = (mem.allocation_pct ?? 100) / 100;
      const memberStart = mem.start_date
        ? parseLocalDate(mem.start_date)
        : parseLocalDate('2020-01-01');
      const memberEnd = mem.end_date ? parseLocalDate(mem.end_date) : parseLocalDate('2099-12-31');
      const monthlyCost = monthlyRate * allocPct;

      for (const month of allMonths) {
        const [yr, mo] = month.split('-').map(Number);
        const monthStart = new Date(yr, mo - 1, 1);
        const monthEnd = new Date(yr, mo, 0);
        if (monthEnd < memberStart || monthStart > memberEnd) continue;
        ratesCogs[month] += monthlyCost;
      }
    }

    // Past months: use actual payroll when available, fall back to rates
    // Future months: always use rates
    for (const month of allMonths) {
      const isForecast = month > todayMonth;
      if (isForecast) {
        cogs[month] = ratesCogs[month];
      } else if (actualPayroll[month] > 0) {
        cogs[month] = actualPayroll[month];
      } else {
        cogs[month] = ratesCogs[month];
      }
    }

    // Project future OpEx using trailing average of historical months with data
    const historicalOpexMonths = allMonths.filter((mo) => mo <= todayMonth && opex[mo] > 0);
    if (historicalOpexMonths.length > 0) {
      const avgOpex =
        historicalOpexMonths.reduce((s, mo) => s + opex[mo], 0) / historicalOpexMonths.length;
      for (const month of allMonths) {
        if (month > todayMonth) {
          opex[month] = Math.round(avgOpex * 100) / 100;
        }
      }
    }

    // Pro-rate current month OpEx (partial month → projected full month)
    if (allMonths.includes(todayMonth) && opex[todayMonth] > 0) {
      const dayOfMonth = now.getDate();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      if (dayOfMonth < daysInMonth) {
        opex[todayMonth] = Math.round((opex[todayMonth] / dayOfMonth) * daysInMonth * 100) / 100;
      }
    }

    // Build P&L rows
    const warnings: string[] = [];
    let ytdRevenue = 0;
    let ytdCogs = 0;
    let ytdOpex = 0;
    let historicalMonthsWithData = 0;
    let totalSavingsRate = 0;
    const MONTH_NAMES = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const rows: MonthlyPnLRow[] = allMonths.map((month) => {
      const isForecast = month > todayMonth;

      const rev = Math.round(revenue[month] * 100) / 100;
      const cogVal = Math.round(cogs[month] * 100) / 100;
      const grossProfit = Math.round((rev - cogVal) * 100) / 100;
      const grossMarginPct = rev > 0 ? Math.round((grossProfit / rev) * 1000) / 10 : 0;
      const opexVal = Math.round(opex[month] * 100) / 100;
      const netInc = Math.round((grossProfit - opexVal) * 100) / 100;
      const target = Math.round(((rev * savingsTargetPct) / 100) * 100) / 100;
      const surp = Math.round((netInc - target) * 100) / 100;

      if (!isForecast) {
        ytdRevenue += rev;
        ytdCogs += cogVal;
        ytdOpex += opexVal;

        if (rev > 0) {
          historicalMonthsWithData += 1;
          totalSavingsRate += (netInc / rev) * 100;
        }

        // Warnings
        if (rev === 0 && cogVal > 0) {
          const moIdx = parseInt(month.split('-')[1], 10) - 1;
          warnings.push(
            `${MONTH_NAMES[moIdx]} has $0 revenue but ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(cogVal)} in COGS — missing invoices?`
          );
        }
      }

      return {
        month,
        revenue: rev,
        cogs: cogVal,
        grossProfit,
        grossMarginPct,
        opex: opexVal,
        netIncome: netInc,
        savingsTarget: target,
        surplus: surp,
        isForecast,
      };
    });

    const ytdGrossProfit = Math.round((ytdRevenue - ytdCogs) * 100) / 100;
    const ytdNetIncome = Math.round((ytdGrossProfit - ytdOpex) * 100) / 100;
    const ytdSavingsTarget = Math.round(((ytdRevenue * savingsTargetPct) / 100) * 100) / 100;
    const ytdSurplus = Math.round((ytdNetIncome - ytdSavingsTarget) * 100) / 100;
    const avgMonthlySavingsRate =
      historicalMonthsWithData > 0
        ? Math.round((totalSavingsRate / historicalMonthsWithData) * 10) / 10
        : 0;

    // Check for clients without COGS
    const teamsWithMembers = new Set((membersData ?? []).map((member: any) => member.team_id));
    const teamsWithoutCogs = teamIds.filter((id: string) => !teamsWithMembers.has(id));
    if (teamsWithoutCogs.length > 0) {
      warnings.push(
        `${teamsWithoutCogs.length} client(s) have no team members with rates set — COGS may be understated`
      );
    }

    return {
      rows,
      ytdRevenue: Math.round(ytdRevenue * 100) / 100,
      ytdCogs: Math.round(ytdCogs * 100) / 100,
      ytdGrossProfit,
      ytdOpex: Math.round(ytdOpex * 100) / 100,
      ytdNetIncome,
      ytdSavingsTarget,
      ytdSurplus,
      avgMonthlySavingsRate,
      warnings,
    };
  },
};

export default financeClientsService;
